import {
  Container,
  EntityStatusInput,
  PageWrapper,
  SaveAndCancel,
  StickyActionsHeader,
} from "@/components";
import {
  CONTENT_WIDTH,
  EntityStatus,
  FieldType,
  FormType,
  GUTTER_SIZE,
} from "@/enums";
import {
  buildProjectStatusOptions,
  fieldNameValidator,
  handleServerErrorResponse,
  routes,
  selectFilterHelper,
} from "@/helpers";
import { useFlatFormAvailability } from "@/hooks";
import {
  useCreateFormPropertyMutation,
  useGetAdminFlatFormsQuery,
  useGetAdminFormsQuery,
  useGetProductCategoryFormsQuery,
} from "@/services";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Row,
  Select,
  Spin,
  Typography,
} from "antd";
import _ from "lodash";
import { useEffect, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryParam } from "use-query-params";
import {
  NewFormFieldProvider,
  useNewFormFieldContext,
} from "./NewFormFieldProvider";
import { AttachmentFormField } from "./components/AttachmentFormField";
import { AutoCompleteFormField } from "./components/AutoCompleteFormField";
import { DateFormField } from "./components/DateFormField";
import { DateRangeFormField } from "./components/DateRangeFormField";
import { GenericFormField } from "./components/GenericFormField";
import { NumberFormField } from "./components/NumberFormField";
import { OptionsFormField } from "./components/OptionsFormField";
import { PercentCompleteSliderFormField } from "./components/PercentCompleteSliderFormField";
import { SwitchField } from "./components/SwitchField";
import {
  Backdrop,
  ElementTitle,
  FormInput,
  FormItemContent,
  FormItemRow,
  FormSelect,
  SelectedFieldTypeWrapper,
  Tile,
  TileTitle,
} from "./styled";
import useFieldTypes from "./useFieldTypes";

const { Title } = Typography;

const NewFormFieldContent = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { formType } = useParams();
  const [parent] = useQueryParam("parent");
  const fieldTypes = useFieldTypes();
  const { formRef, selectedFieldType, setSelectedFieldType } =
    useNewFormFieldContext();

  const { data: { flatForms, categories } = {}, isFetching } =
    useGetAdminFlatFormsQuery();
  const { refetch: refetchAdminForms } = useGetAdminFormsQuery();
  const { refetch: refetchForms } = useGetProductCategoryFormsQuery();
  const [createFormProperty, { isLoading: isSaving }] =
    useCreateFormPropertyMutation();

  const [parentId, setParentId] = useState(parent);
  const [availableValues, setAvailableValues] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const { availableParent, availableDateFields } =
    useFlatFormAvailability(flatForms);

  const handleSave = (form) => {
    const formType = form.formType;
    let values = form.options;
    const data = {
      ...form,
      label: { fallback: form.label },
      placeholder: form.placeholder
        ? { fallback: form.placeholder }
        : undefined,
      requiredValidationText: form.requiredValidationText
        ? { fallback: form.requiredValidationText }
        : undefined,
      status: form.property.status,
    };
    delete data.options;

    if (values) {
      values = values.map((item) => {
        item.label = {
          fallback: item.fallback,
        };
        delete item.fallback;

        return item;
      });
    }

    createFormProperty({ form: formType, property: data, values })
      .unwrap()
      .then(() => {
        refetchForms();
        refetchAdminForms();
        navigate(routes.admin.forms.get(formType));
      })
      .catch((response) => {
        if (response?.originalStatus === 409) {
          toast.error(
            intl.formatMessage({ id: "newFormField.fieldNameErrorMessage" })
          );
        } else {
          handleServerErrorResponse(response, intl);
        }
      });
  };

  const handleFieldTypeSelection = (fieldType) => {
    formRef.current.setFieldsValue({ fieldType });
    setSelectedFieldType(fieldType);
  };

  let selectedFieldTypeForm = null;
  switch (selectedFieldType) {
    case FieldType.Attachment:
    case FieldType.MultiAttachment:
      selectedFieldTypeForm = <AttachmentFormField />;
      break;
    case FieldType.DropDown:
    case FieldType.Checkbox:
    case FieldType.MultiSelectDropDown:
      selectedFieldTypeForm = (
        <OptionsFormField availableDependantValues={availableValues} />
      );
      break;
    case FieldType.AutoComplete:
    case FieldType.MultiSelectAutoComplete:
      selectedFieldTypeForm = <AutoCompleteFormField />;
      break;
    case FieldType.Number:
      selectedFieldTypeForm = <NumberFormField />;
      break;
    case FieldType.PercentCompleteSlider:
      selectedFieldTypeForm = <PercentCompleteSliderFormField />;
      break;
    case FieldType.Date:
      selectedFieldTypeForm = (
        <DateFormField availableDateFields={availableDateFields} />
      );
      break;
    case FieldType.DateRange:
      selectedFieldTypeForm = (
        <DateRangeFormField availableParent={availableParent} />
      );
      break;
    case FieldType.Switch:
      selectedFieldTypeForm = <SwitchField availableParent={availableParent} />;
      break;
    case FieldType.Text:
    case FieldType.TextArea:
    default:
      selectedFieldTypeForm = <GenericFormField />;
      break;
  }

  const onParentIdChange = (value) => {
    setParentId(value);
    formRef.current.setFieldsValue({ DependsOnPropertyId: value });
  };

  const onFormTypeChange = (value) => {
    formRef.current.setFieldsValue({ formType: value });
  };

  useEffect(() => {
    if (!isFetching) {
      const availableStatuses = buildProjectStatusOptions().map((option) => ({
        ...option,
        fallback: intl.formatMessage({ id: option.translationId }),
      }));

      setAvailableStatuses(availableStatuses);
    }
  }, [isFetching, intl]);

  useEffect(() => {
    if (flatForms && parentId) {
      const index = flatForms.findIndex((x) => x.property.id === parentId);
      if (index >= 0 && flatForms[index].values) {
        const values = flatForms[index].values.map((item) => ({
          value: item.id,
          text: intl.formatMessage({
            id: item.label.id,
            defaultMessage: item.label.fallback,
          }),
        }));
        if (flatForms[index].property?.fieldType === FieldType.Switch) {
          values.push({ value: "true", text: "True" });
          values.push({ value: "false", text: "False" });
        }
        setAvailableValues(values);
      }
    } else {
      setAvailableValues([]);
    }
  }, [intl, parentId, flatForms]);

  let step = 1;

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "newFormField.contentTitle" })}
      backButtonRoute={routes.admin.forms.get(formType)}
      pageActions={
        <StickyActionsHeader
          rightChildren={
            <SaveAndCancel
              pageName="new-form-field"
              saveOnClick={() => formRef.current.submit()}
              loading={isSaving}
              cancelOnClick={() => navigate(routes.admin.forms.get(formType))}
            />
          }
        />
      }
      noStyle
    >
      <Container>
        {!!isFetching && <Spin />}
        {!isFetching && (
          <Form
            ref={formRef}
            name="basic"
            onFinish={handleSave}
            initialValues={{
              status: EntityStatus.Preview,
            }}
            layout="vertical"
          >
            <FormItemRow>
              <Title level={2}>
                <FormattedNumber value={step++} />
              </Title>
              <FormItemContent>
                <Form.Item
                  data-test-id="NewFormField-Form-Item-Name"
                  name="Name"
                  onChange={(e) => fieldNameValidator(e.target.value, intl)}
                  normalize={(value) => _.camelCase(value)}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "validationErrors.nameRequired",
                      }),
                    },
                  ]}
                  label={
                    <ElementTitle level={3}>
                      <FormattedMessage id="newFormField.name" />
                    </ElementTitle>
                  }
                >
                  <FormInput />
                </Form.Item>
              </FormItemContent>
            </FormItemRow>

            <FormItemRow>
              <Title level={2}>
                <FormattedNumber value={step++} />
              </Title>
              <FormItemContent>
                <Form.Item
                  name="formType"
                  initialValue={formType.toLowerCase()}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "validationErrors.formTypeRequired",
                      }),
                    },
                  ]}
                  label={
                    <ElementTitle level={3}>
                      <FormattedMessage id="newFormField.formTypeTitle" />
                    </ElementTitle>
                  }
                >
                  <FormSelect
                    data-test-id="new-form-field-select-form-type"
                    onChange={onFormTypeChange}
                  >
                    {Object.keys(FormType).map((key) => (
                      <Select.Option
                        key={`form-type-${key}`}
                        value={key.toLowerCase()}
                      >
                        <FormattedMessage
                          id={`enum.formType.${key.toLowerCase()}`}
                        />
                      </Select.Option>
                    ))}
                  </FormSelect>
                </Form.Item>
              </FormItemContent>
            </FormItemRow>

            <FormItemRow>
              <Title level={2}>
                <FormattedNumber value={step++} />
              </Title>
              <FormItemContent>
                <Form.Item
                  name="CategoryId"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "validationErrors.categoryRequired",
                      }),
                    },
                  ]}
                  label={
                    <ElementTitle level={3}>
                      <FormattedMessage id="newFormField.category" />
                    </ElementTitle>
                  }
                >
                  <FormSelect
                    allowClear
                    placeholder={intl.formatMessage({
                      id: "newFormField.categoryPlaceHolder",
                    })}
                  >
                    {categories?.map((opt) => (
                      <Select.Option key={opt.id} value={opt.id}>
                        {intl.formatMessage({
                          id: opt?.label?.id,
                          defaultMessage: opt?.label?.fallback,
                        })}
                      </Select.Option>
                    ))}
                  </FormSelect>
                </Form.Item>
              </FormItemContent>
            </FormItemRow>

            <FormItemRow>
              <Title level={2}>
                <FormattedNumber value={step++} />
              </Title>
              <FormItemContent>
                <Form.Item
                  name="DependsOnPropertyId"
                  initialValue={parent || undefined}
                  label={
                    <ElementTitle level={3}>
                      <FormattedMessage id="newFormField.parentFieldTitle" />
                    </ElementTitle>
                  }
                >
                  <FormSelect
                    data-test-id="new-form-field-select-available-parents"
                    onChange={onParentIdChange}
                    allowClear
                  >
                    {availableParent?.key && availableParent?.items?.length && (
                      <Select.OptGroup
                        label={availableParent.key}
                        key={availableParent.key}
                      >
                        {availableParent.items.map(({ value, text }) => (
                          <Select.Option key={value} value={value}>
                            {text}
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    )}
                  </FormSelect>
                </Form.Item>
              </FormItemContent>
            </FormItemRow>

            <FormItemRow>
              <Title level={2}>
                <FormattedNumber value={step++} />
              </Title>
              <FormItemContent>
                <Form.Item
                  name="dependsOnPropertyValues"
                  label={
                    <ElementTitle level={3}>
                      <FormattedMessage id="newFormField.dependentValuesTitle" />
                    </ElementTitle>
                  }
                >
                  <FormSelect
                    mode="multiple"
                    disabled={!parentId}
                    allowClear
                    placeholder={intl.formatMessage({
                      id: "newFormField.dependentValuesPlaceholder",
                    })}
                    filterOption={selectFilterHelper}
                  >
                    {availableValues.map((x) => (
                      <Select.Option value={x.value} key={x.value}>
                        {x.text}
                      </Select.Option>
                    ))}
                  </FormSelect>
                </Form.Item>
              </FormItemContent>
            </FormItemRow>

            <FormItemRow>
              <Title level={2}>
                <FormattedNumber value={step++} />
              </Title>
              <FormItemContent>
                <Form.Item
                  name="TrackHistory"
                  valuePropName="checked"
                  label={
                    <ElementTitle level={3}>
                      <FormattedMessage id="newFormField.fieldHistoryLabel" />
                    </ElementTitle>
                  }
                >
                  <Checkbox>
                    {intl.formatMessage({
                      id: "newFormField.fieldHistoryLabel",
                    })}
                  </Checkbox>
                </Form.Item>
              </FormItemContent>
            </FormItemRow>

            <FormItemRow>
              <Title level={2}>
                <FormattedNumber value={step++} />
              </Title>
              <FormItemContent>
                <Form.Item
                  name="fieldType"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "validationErrors.fieldTypeRequired",
                      }),
                    },
                  ]}
                  label={
                    <ElementTitle level={3}>
                      <FormattedMessage id="newFormField.fieldTypeTitle" />
                    </ElementTitle>
                  }
                >
                  <>
                    {selectedFieldType && (
                      <SelectedFieldTypeWrapper>
                        <h2>
                          {intl.formatMessage({
                            id: `enum.fieldType.${selectedFieldType.toLowerCase()}`,
                          })}
                        </h2>
                        <Button
                          data-test-id="new-form-field-select-field-type-button"
                          type="link"
                          onClick={() => setSelectedFieldType(null)}
                        >
                          {intl.formatMessage({
                            id: "newFormField.changeFieldTypeButton",
                          })}
                        </Button>
                      </SelectedFieldTypeWrapper>
                    )}
                    {!selectedFieldType ? (
                      <Row gutter={GUTTER_SIZE.large}>
                        {fieldTypes.map((field) => (
                          <Col
                            key={`field-${field.fieldType}`}
                            xs={CONTENT_WIDTH.max}
                            sm={CONTENT_WIDTH.half}
                            xl={CONTENT_WIDTH.oneQuarter}
                          >
                            <Tile
                              data-test-id="new-form-field-select-field-type-tile"
                              id={`tile-${field.fieldType}`}
                              onClick={() =>
                                handleFieldTypeSelection(field.fieldType)
                              }
                            >
                              <TileTitle>{field.title}</TileTitle>
                              {field.component}
                              <Backdrop />
                            </Tile>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      selectedFieldTypeForm
                    )}
                  </>
                </Form.Item>
              </FormItemContent>
            </FormItemRow>

            <FormItemRow>
              <Title level={2}>
                <FormattedNumber value={step++} />
              </Title>
              <FormItemContent>
                <Form.Item
                  name="RequiredForStatuses"
                  label={
                    <ElementTitle level={3}>
                      <FormattedMessage id="newFormField.requiredForStatuses" />
                    </ElementTitle>
                  }
                >
                  <FormSelect
                    mode="multiple"
                    allowClear
                    placeholder={intl.formatMessage({
                      id: "newFormField.requiredForStatusesPlaceholder",
                    })}
                    filterOption={selectFilterHelper}
                  >
                    {availableStatuses.map((x) => (
                      <Select.Option value={x.id} key={x.id}>
                        {x.fallback}
                      </Select.Option>
                    ))}
                  </FormSelect>
                </Form.Item>
              </FormItemContent>
            </FormItemRow>

            <FormItemRow>
              <Title level={2}>
                <FormattedNumber value={step++} />
              </Title>
              <FormItemContent>
                <Form.Item
                  name="LockOnSubmission"
                  valuePropName="checked"
                  label={
                    <ElementTitle level={3}>
                      <FormattedMessage id="newFormField.lockAfterSubmitTitle" />
                    </ElementTitle>
                  }
                >
                  <Checkbox>
                    {intl.formatMessage({
                      id: "newFormField.lockAfterSubmitTitle",
                    })}
                  </Checkbox>
                </Form.Item>
              </FormItemContent>
            </FormItemRow>
            <EntityStatusInput parentComponent="new-form-field" />
          </Form>
        )}
      </Container>
    </PageWrapper>
  );
};

export const NewFormField = () => (
  <NewFormFieldProvider>
    <NewFormFieldContent />
  </NewFormFieldProvider>
);
